
import { Component, Mixins } from 'vue-property-decorator'
import IconsMixin from '@/mixins/IconsMixin.vue'
import AuthMixin from '@/mixins/AuthMixin.vue'
import LoadingPage from '@/components/LoadingPage.vue'
import {
  Company,
  CompanyCollection,
  CompanyTypeEnum,
  Order,
  OrderCollection,
  PriceList,
  PriceListCollection,
  PriceListEntry,
  MasterCatalog,
  MasterCatalogCollection
} from '@/firestore'
import LabeledValue from '@/components/LabeledValue.vue'
import store, { Actions } from '../../../store'
import EnableVendorModal from '@/components/Onboarding/EnableVendorModal.vue'

interface CategoryEntries {
  category: string,
  categoryName: string,
  entries: PriceListEntry[]
}

@Component({
  components: {LabeledValue, LoadingPage, EnableVendorModal}
})
export default class VendorDetails extends Mixins(IconsMixin, AuthMixin) {
  public wVendor: { data: Company | null } = {data: null}
  public masterCatalog: MasterCatalog = {} as MasterCatalog
  public categoryMappedEntriesFlattened = [] as any[]
  public orders: Order[] = []
  public priceLists: PriceList[] = []
  public selectedPriceListIndex: number = 0

  public tab = null

  // modal props
  public showDisableModal = false
  public showEnableVendorModal = false
  public showErrorModal = false
  public errorModalMessage = ''
  public action = ''

  private priceListUnsub: () => void = () => {
  }

  get items(): { tab: string, content: PriceList }[] {
    return this.priceLists.map(priceList => {
      return {tab: priceList.vendorRepName, content: priceList}
    })
  }

  get selectedPriceList(): PriceList | null {
    if (this.priceLists && this.priceLists[this.selectedPriceListIndex]) {
      return this.priceLists[this.selectedPriceListIndex]
    } else {
      return null
    }
  }

  get vendor(): Company | null {
    return this.wVendor.data
  }

  public async mounted() {
    try {
      const firestore = (await this.$firebase).firestore()
      const vendorId = this.$route.params.id
      const restaurantId = this.userCompany.id

      const companyCollection = new CompanyCollection(firestore)
      this.wVendor = companyCollection.watchCompany(vendorId)

      const priceListCollection = new PriceListCollection(firestore)
      this.priceLists = priceListCollection.watchPriceListsByRestaurantAndVendor(
          restaurantId,
          vendorId,
          (_pl, unsub) => {
            if (unsub) {
              this.priceListUnsub = unsub
            }
          }
      )

      this.selectedPriceListIndex = 0

      const masterCatalogCollection = new MasterCatalogCollection(firestore)
      const masterCatalog = await masterCatalogCollection.getByCompanyId(vendorId)
      if (masterCatalog) {
        this.masterCatalog = masterCatalog

        const categoryPrefix = 'Food, Beverages & Tobacco > Beverages > Alcoholic Beverages'
        const catagories = [
          `${categoryPrefix} > Liquor & Spirits`,
          `${categoryPrefix} > Wine`,
          `${categoryPrefix} > Beer`
        ]

        this.categoryMappedEntriesFlattened = this.getEntriesCategoryMapping(this.masterCatalog.entries, catagories)
      }

      const orderCollection = new OrderCollection(firestore)
      this.orders = orderCollection.watchOrdersByRestaurantAndVendor(restaurantId, vendorId)
    } catch (error) {
      const err = new Error('You do not have permissions to view that page')
      return store.dispatch(Actions.SET_ERROR, [err, this.$router])
    }
  }

  public async edit() {
    if (!this.selectedPriceList || !this.selectedPriceList.id) {
      console.error('no priceList id found')
      return await this.$router.go(0)
    }

    return await this.$router.push({name: 'vendor-rep-edit', params: {id: this.selectedPriceList.id}})
  }

  public async viewOrder(order: Order) {
    if (order.restaurantOrderId) {
      return await this.$router.push({name: 'order-details', params: {id: order.restaurantOrderId}})
    } else {
      const err = new Error('You do not have permissions to view that page')
      return store.dispatch(Actions.SET_ERROR, [err, this.$router])
    }
  }

  public confirmDisable() {
    this.showDisableModal = false
    this.disableVendor()
  }

  public updateSelected(index: any) {
    this.selectedPriceListIndex = index
  }

  public reminderManagement() {
    if (!this.vendor) {
      const err = new Error('No Vendor Found')
      return store.dispatch(Actions.SET_ERROR, [err, this.$router])
    }

    return this.$router.push({name: 'reminder-management', params: {id: this.vendor.id}})
  }

  public async disableVendor() {
    if (!this.selectedPriceList || !this.isGeneralManagerOrGreater) {
      return
    }

    try {
      const collection = new PriceListCollection((await this.$firebase).firestore())
      await collection.save({...this.selectedPriceList, active: false} as PriceList)
      if (!(this.priceLists && this.priceLists.length)) {
        this.$router.push({name: 'vendor-list'})
      }
    } catch (err) {
      this.errorModalMessage = err.message
      this.showErrorModal = true
    }
  }

  public destroyed() {
    if (this.priceListUnsub) {
      this.priceListUnsub()
    }
  }

  private getEntriesCategoryMapping(entries: PriceListEntry[], categories: string[]): any[] {
    const other = 'Other'
    // create blank mapping
    const categoryMappingTemplate = categories.reduce((map, category) => (
        {...map, [category]: []}
    ), {[other]: []} as Record<string, PriceListEntry[]>)

    // populate mapping with entries
    const categoryMapping = entries.reduce((map, entry) => {
      const categoryIndex = this.getCategoryIndex(entry.category ? entry.category : '', categories)
      const category = categoryIndex > -1 ? categories[categoryIndex] : other
      map[category].push(entry)
      return map
    }, categoryMappingTemplate)

    // create collator comparator
    const collator = new Intl.Collator('en', {numeric: true})

    // sort the mapped entry arrays
    const sortedCategoryMapping = Object.entries(categoryMapping).reduce((map, [cat, entryItem]) => ({
      ...map,
      [cat]: entryItem.sort((a: any, b: any) => collator.compare(a.description, b.description))
    }), {}) as { [category: string]: PriceListEntry[] }

    // create array of category mappings
    const sortedCategoryEntries = categories.map(category => ({
      category,
      categoryName: category.split(' > ').pop(),
      entries: sortedCategoryMapping[category]
    }))
    sortedCategoryEntries.push({
      category: other,
      categoryName: other,
      entries: sortedCategoryMapping[other]
    })

    const sortedCategoryEntriesFlattened: any[] = []
    sortedCategoryEntries.forEach(categoryEntry => {
      if (categoryEntry.entries && categoryEntry.entries.length) {
        sortedCategoryEntriesFlattened.push(categoryEntry.categoryName)
      }
      categoryEntry.entries.forEach(entry => {
        sortedCategoryEntriesFlattened.push(entry)
      })
    })

    return sortedCategoryEntriesFlattened
  }

  private getCategoryIndex(category: string, categories: string[]) {
    const exactIndex = categories.indexOf(category)
    return exactIndex > -1
        ? exactIndex
        : categories.findIndex(categoryItem => category.startsWith(categoryItem))
  }
}
